
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormData, ProtectedCharacteristic } from '@/hooks/useFormState';
import { ArrowRight, ArrowLeft } from 'lucide-react';

interface ProtectedCharacteristicsProps {
  formData: FormData;
  updateFormData: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onPrev: () => void;
  isValid: boolean;
}

const ProtectedCharacteristicsStep: React.FC<ProtectedCharacteristicsProps> = ({
  formData,
  updateFormData,
  onNext,
  onPrev,
  isValid
}) => {
  const handleToggleCharacteristic = (characteristic: ProtectedCharacteristic) => {
    const currentCharacteristics = [...formData.protectedCharacteristics];
    if (currentCharacteristics.includes(characteristic)) {
      updateFormData('protectedCharacteristics', currentCharacteristics.filter(c => c !== characteristic));
    } else {
      updateFormData('protectedCharacteristics', [...currentCharacteristics, characteristic]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onNext();
    }
  };

  const characteristics: Array<{
    id: ProtectedCharacteristic;
    label: string;
  }> = [
    { id: 'age', label: 'Age' },
    { id: 'disability', label: 'Disability' },
    { id: 'genderReassignment', label: 'Gender Reassignment' },
    { id: 'marriageAndCivilPartnership', label: 'Marriage & Civil Partnership' },
    { id: 'pregnancyAndMaternity', label: 'Pregnancy & Maternity' },
    { id: 'race', label: 'Race' },
    { id: 'religionOrBelief', label: 'Religion or Belief' },
    { id: 'sex', label: 'Sex' },
    { id: 'sexualOrientation', label: 'Sexual Orientation' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in w-full">
      <div className="space-y-1 sm:space-y-2 text-center">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Protected Characteristics</h2>
        <p className="text-sm sm:text-base text-muted-foreground">Please select the protected characteristic(s) that apply to your situation</p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        {characteristics.map((characteristic) => (
          <div
            key={characteristic.id}
            className="flex items-center space-x-2 bg-white p-2.5 sm:p-3 rounded-md transition-all hover:bg-gray-50 border border-gray-200 shadow-sm w-full"
          >
            <Checkbox
              id={characteristic.id}
              checked={formData.protectedCharacteristics.includes(characteristic.id)}
              onCheckedChange={() => handleToggleCharacteristic(characteristic.id)}
              className="h-5 w-5"
            />
            <Label 
              htmlFor={characteristic.id}
              className="font-medium cursor-pointer w-full text-sm sm:text-base"
            >
              {characteristic.label}
            </Label>
          </div>
        ))}
      </div>

      <div className="flex space-x-3 sm:space-x-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onPrev}
          className="flex-1 group"
        >
          <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 group"
          disabled={!isValid}
        >
          Continue
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </form>
  );
};

export default ProtectedCharacteristicsStep;
